import { getCookie } from 'cookies-next'
import { useCallback, useState } from 'react'

import { SPLIT_NAMES, type SplitTreatments } from '@/constants/splits'
import type { AppliedCoupon } from '@/hooks/useCoupon'
import type {
  PermanentDiscountData,
  PremiumInterval,
  PriceData,
  ReferralDiscountData,
} from '@flows-ui/services/QuoteDataContext.types'
import type { QuoteDataContextValue } from '@flows-ui/stepRenderer'
import { findPriceEntry, getReferralCreditForInterval } from '@flows-ui/utils/discounts'
import { getSelectedCoveragePlan } from '@flows-ui/utils/getSelectedCoveragePlan'
import { cookieNames } from '@helpers/cookies/utils'

import type { FlowStepPageConfig } from './types'

export const useFlowDiscounts = ({
  config,
  appliedCoupon,
  featureFlags,
  flowValues,
  isAuthenticated,
  latestPriceData,
  premiumInterval,
  productGeneration,
}: {
  config: FlowStepPageConfig
  appliedCoupon: AppliedCoupon | null
  featureFlags: SplitTreatments
  flowValues: Record<string, unknown>
  isAuthenticated: boolean
  latestPriceData: PriceData | undefined
  premiumInterval: PremiumInterval
  productGeneration: QuoteDataContextValue['productGeneration']
}): {
  grossPremium: number
  permanentDiscount: PermanentDiscountData | null
  permanentDiscountLost: boolean
  referralDiscount: ReferralDiscountData | null
  selectedPlan: ReturnType<typeof getSelectedCoveragePlan>
} => {
  const [loggedInDuringFlow, setLoggedInDuringFlow] = useState(false)
  const revokePermanentDiscount = useCallback(() => setLoggedInDuringFlow(true), [])

  const isExistingCustomer = isAuthenticated || loggedInDuringFlow

  const products = (flowValues.products ?? {}) as Record<
    string,
    { configuration_parameters?: Record<string, unknown> } | undefined
  >
  const configurationParameters = products[config.productKey]?.configuration_parameters ?? {}
  const selectedPlan = getSelectedCoveragePlan({ configurationParameters, productGeneration })

  const effectiveInterval = config.fixedPriceInterval ?? premiumInterval

  const priceEntry = findPriceEntry({
    premiumInterval: effectiveInterval,
    priceData: latestPriceData,
    productKey: config.productKey,
    tariffLine:
      typeof configurationParameters.tariff_line === 'string'
        ? configurationParameters.tariff_line
        : undefined,
    configurationId: selectedPlan?.itemKey?.current,
  })

  const parsedGrossPremium = Number.parseFloat(priceEntry?.gross_premium ?? '0')
  const grossPremium = Number.isFinite(parsedGrossPremium) ? parsedGrossPremium : 0
  const parsedDiscountAmount = Number(priceEntry?.discount_amount ?? 0)
  const discountAmount = Number.isFinite(parsedDiscountAmount) ? parsedDiscountAmount : 0
  const referralToken = getCookie(cookieNames.referralCode)
  const hasReferralToken = typeof referralToken === 'string' && referralToken !== ''
  const hasCoupon = appliedCoupon !== null

  const permanentDiscountTreatment = featureFlags[SPLIT_NAMES.webPermanentDiscount]

  const qualifies =
    config.supportsPermanentDiscount === true &&
    permanentDiscountTreatment === 'on' &&
    !hasReferralToken &&
    !hasCoupon &&
    discountAmount > 0

  const permanentDiscount: PermanentDiscountData | null = config.supportsPermanentDiscount
    ? {
        shown: qualifies && !isExistingCustomer,
        amount: discountAmount,
        token: priceEntry?.discount_token ?? null,
        revoked: isExistingCustomer,
        grossPremium,
        grossPremiumAfterDiscount: Number.parseFloat(
          priceEntry?.gross_premium_after_discount ?? priceEntry?.gross_premium ?? '0'
        ),
        revokePermanentDiscount,
      }
    : null

  const referralDiscount: ReferralDiscountData = {
    referralCreditAmount: getReferralCreditForInterval('P1Y'),
    referralCreditToggledAmount: getReferralCreditForInterval(effectiveInterval),
    grossPremium,
    shouldShowReferralDiscount: hasReferralToken && !hasCoupon,
  }

  return {
    grossPremium,
    permanentDiscount,
    permanentDiscountLost: qualifies && isExistingCustomer,
    referralDiscount,
    selectedPlan,
  }
}
